import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useProvider } from '~/Hooks';
import { PROVIDER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { IOrganisation } from '~/Models/DTO/Organisation.model';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { IErrorResponse } from '~/Models/DTO/Error.models';

export const useRequestOrganisation = (enabled?: boolean) => {
  const [currentOrganisation, setOrganisation] = useState<IOrganisation>({} as any);
  const queryClient = useQueryClient();
  // hooks
  const { currentProvider } = useProvider();
  const result = useQuery(
    [REQUEST_ACTIONS.GET_ORGANISATION, currentProvider?.organisationId],
    async () =>
      requestReactQuery<IOrganisation>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.ORGANISATION, {
          organisationId: currentProvider?.organisationId || '0',
        }),
        config: { method: 'GET' },
      }),
    {
      enabled,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: TIME_VALUES.HOUR,
    },
  );
  const {
    isLoading: isOrganisationLoading,
    error: organisationError,
    dataUpdatedAt: organisationDataUpdatedAt,
    isFetched: isOrganisationFetched,
  } = result;
  const invalidateGetOrganization = () =>
    queryClient.invalidateQueries({ queryKey: [REQUEST_ACTIONS.GET_ORGANISATION] });
  const { error: organisationUpdateError, isLoading: isOrganisationUpdating, mutate } = useMutation(
    async (organisation: IOrganisation) =>
      requestReactQuery<IOrganisation>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.ORGANISATION, {
          organisationId: currentProvider?.organisationId || '0',
        }),
        data: organisation,
        config: { method: 'PATCH' },
      }),
    {
      onSuccess: invalidateGetOrganization,
    },
  );

  const updateOrganisation = (org: IOrganisation) => {
    mutate(org);
    setOrganisation(org);
  };
  useEffect(() => {
    if (result?.data) {
      setOrganisation(result.data);
    }
  }, [result?.data]);

  return {
    isOrganisationLoading,
    organisationError: (organisationError || organisationUpdateError) as IErrorResponse,
    organisation: currentOrganisation,
    organisationDataUpdatedAt,
    isOrganisationFetched,
    updateOrganisation,
    isOrganisationUpdating,
  };
};
